import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _, { noop } from 'underscore';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { ImagePickerContainer } from '@bingads-webui-react/image-picker';
import { TextField } from '@bingads-webui-react/fabric-control';
import LoadingPlaceholder from '../../components/loading-place-holder';
import FinalUrlInput from '../../components/final-url-input';
import ImageSuggestionItemWrapper from './image-suggestion-item-wrapper';
import { getAlertString, isEmptyUrl, isInvalidUrl, isInProgress } from './alert-message';

const itemHeight = 180;
const maximumItemCount = 2000;
const imagePlaceHolder = () => <LoadingPlaceholder />;

const subdomainUrlRegex = /^https?:\/\/([a-z0-9-_]+\.)*[a-z0-9-_]+\.[a-z]{2,63}(?=$|\/)\//i;

const getSubdomain = (url) => {
  const regexResult = url.trim().split(subdomainUrlRegex);
  if (regexResult && regexResult.length > 1) {
    return regexResult[2];
  }
  return url;
};

export const subdomainChangeHandler =
(e, newSubdomain, setNewSubdomain, initialSubdomain, onUpdateSubdomainUrl) => {
  if (newSubdomain !== initialSubdomain &&
    !_.isNull(newSubdomain) && !_.isUndefined(newSubdomain)) {
    const newValue = getSubdomain(newSubdomain);
    setNewSubdomain(newValue);
    onUpdateSubdomainUrl(newValue);
  }
};

const ImageSuggestionPickerContainer = ({
  suggestion,
  dataSource,
  i18n,
  onImageSelectedByUrl,
  onUpdateSubdomainUrl,
  finalUrlDomain,
  finalUrlProtocol,
  finalUrl,
  onUpdateFinalUrlPrefix,
  onUpdateFinalUrl,
  logger,
  enableSubdomain,
  blockImagePickerOnly,
  initialSubdomain,
  onKeyboardEscapeFromImage,
  isLogo,
}) => {
  const alertString = getAlertString(suggestion, i18n);
  const [newSubdomain, setNewSubdomain] = useState(initialSubdomain || '');
  const onSelectionChanged = ([{ fullSrc }], index) => {
    onImageSelectedByUrl(fullSrc);

    const eventMessage = isLogo ? 'UserSelectedWebsiteImageForLogo' : 'UserSelectedWebsiteSuggestionImage';

    logger.trace(`${eventMessage}: ${index};`);
  };
  const onLoadMore = () => {
    dataSource.loadMore();
  };

  useEffect(() => {
    if (suggestion && !_.isEmpty(suggestion.images)) {
      const { hasMore, images } = suggestion;
      const imagesList = images.map(({ fullUrl, thumbnailUrl }) => ({
        src: thumbnailUrl,
        fullSrc: fullUrl,
        id: parseInt(_.uniqueId(), 10),
      }));

      dataSource.reload(imagesList, hasMore);
    } else {
      dataSource.reload([]);
    }
  }, [suggestion, dataSource]);

  const showSuggestionError = (isInvalidUrl(suggestion) || isEmptyUrl(suggestion));

  return (
    <div className="image-suggestion-picker-container">
      <UiBlocker
        blocking={suggestion && suggestion.inProgress === true}
        loadingText={alertString}
      >
        {showSuggestionError &&
          <FinalUrlInput
            invalidURL={suggestion.invalidURL}
            i18n={i18n}
            finalUrl={finalUrl}
            finalUrlProtocol={finalUrlProtocol}
            alertString={alertString}
            onUpdateFinalUrl={onUpdateFinalUrl}
            onUpdateFinalUrlPrefix={onUpdateFinalUrlPrefix}
          />
        }
        {!isInProgress(suggestion) && !showSuggestionError && alertString &&
        <div className="alert alert-info" style={{ margin: '3px 0' }}>
          <div className="alert-image" />
          <div className="alert-info-inline">
            {alertString}
          </div>
        </div>
          }
        {suggestion && suggestion.images && !_.isEmpty(suggestion.images) &&
        <div className="found-image-message">
          {i18n.getString('ImageExperience_ImageSuggestion_ImagesFound')}
        </div>
          }
        {!_.isEmpty(suggestion) && !showSuggestionError && enableSubdomain &&
        <div className="subdomain-fields">
          <TextField
            disabled
            className="final-url"
            value={finalUrlDomain}
            ariaLabel={i18n.getString(_TL_('Final url full domain'))}
            styles={{
                  root: {
                    display: 'inline-block',
                    width: 260,
                  },
                  field: {
                    paddingRight: 8,
                    paddingLeft: 32,
                  },
                }}
            iconProps={{
                  iconName: 'Search',
                  styles: {
                    root: {
                      left: 8,
                      right: 0,
                    },
                  },
                }}
          />
          <div className="divider">
            {'/'}
          </div>
          <TextField
            className="subdomain"
            value={newSubdomain}
            placeholder={newSubdomain ? '' : i18n.getString('ImageExperience_ImageSuggestion_Subdomain_Placeholder')}
            onChange={(e, input) => setNewSubdomain(input)}
            onBlur={e => subdomainChangeHandler(
                e, newSubdomain, setNewSubdomain,
                initialSubdomain, onUpdateSubdomainUrl
                )}
            disabled={blockImagePickerOnly}
            styles={{
                  root: {
                    display: 'inline-block',
                    width: 260,
                  },
                }}
          />
        </div>
          }
        <UiBlocker
          blocking={blockImagePickerOnly}
          loadingText={i18n.getString('ImageExperience_InProgress')}
        >
          <ImagePickerContainer
            dataSource={dataSource}
            isMultiSelection={false}
            onLoadMore={onLoadMore}
            isAutoLoadMore
            onSelectionChanged={onSelectionChanged}
            i18n={i18n}
            isLazyLoadEnabled
            maximizeFrame
            itemHeight={itemHeight}
            imagePlaceHolder={imagePlaceHolder}
            imageListItemWrapper={ImageSuggestionItemWrapper}
            maximumItemCount={maximumItemCount}
            onEscapeFromImage={onKeyboardEscapeFromImage}
          />
        </UiBlocker>
      </UiBlocker>
    </div>
  );
};

ImageSuggestionPickerContainer.propTypes = {
  suggestion: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.shape({
      fullUrl: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string.isRequired,
    })),
    inProgress: PropTypes.bool,
  }),
  dataSource: PropTypes.shape({
    reload: PropTypes.func.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  imageRepository: PropTypes.shape({
    bulkSaveImagesByUrl: PropTypes.func.isRequired,
  }).isRequired,
  onImageSelectedByUrl: PropTypes.func.isRequired,
  onUpdateSubdomainUrl: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    trace: PropTypes.func,
  }).isRequired,
  enableSubdomain: PropTypes.bool,
  blockImagePickerOnly: PropTypes.bool,
  finalUrlDomain: PropTypes.string,
  onUpdateFinalUrlPrefix: PropTypes.func,
  onUpdateFinalUrl: PropTypes.func,
  finalUrl: PropTypes.string,
  finalUrlProtocol: PropTypes.string,
  initialSubdomain: PropTypes.string,
  onKeyboardEscapeFromImage: PropTypes.func,
  isLogo: PropTypes.bool,
};

ImageSuggestionPickerContainer.defaultProps = {
  suggestion: {},
  enableSubdomain: false,
  blockImagePickerOnly: false,
  finalUrlDomain: null,
  finalUrl: '',
  finalUrlProtocol: '',
  onUpdateFinalUrlPrefix: noop,
  onUpdateFinalUrl: noop,
  initialSubdomain: null,
  onKeyboardEscapeFromImage: noop,
  isLogo: false,
};

export default ImageSuggestionPickerContainer;
